/**
 * MobileBottomNav - Bottom tab bar for mobile navigation
 *
 * Fixed at the bottom of the screen on mobile (<768px).
 * 4 tabs: Chat, Chats, Docs, Mas.
 * Always visible (z-60).
 * Each tab switches to a fullscreen mobile view.
 */

import React from 'react';
import { MessageSquare, MessagesSquare, FileText, Settings } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export type MobileView = 'chat' | 'chats' | 'docs' | 'more';

interface MobileBottomNavProps {
  mobileView: MobileView;
  selectedDocsCount: number;
  onTabPress: (tab: MobileView) => void;
}

const tabs: { id: MobileView; icon: React.FC<{ className?: string }>; label: string }[] = [
  { id: 'chat', icon: MessageSquare, label: 'Chat' },
  { id: 'chats', icon: MessagesSquare, label: 'Chats' },
  { id: 'docs', icon: FileText, label: 'Docs' },
  { id: 'more', icon: Settings, label: 'Más' },
];

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  mobileView,
  selectedDocsCount,
  onTabPress,
}) => {
  const { theme } = useTheme();

  return (
    <nav
      className={`
        fixed bottom-0 inset-x-0 z-[60]
        hidden max-md:flex
        items-stretch
        border-t ${theme.colors.border.primary} ${theme.colors.bg.primary}
      `}
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      role="tablist"
      aria-label="Navegacion principal"
      data-mobile-nav
    >
      {tabs.map(({ id, icon: Icon, label }) => {
        const active = mobileView === id;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            aria-current={active ? 'page' : undefined}
            aria-label={label}
            onClick={() => onTabPress(id)}
            className={`
              flex-1 flex flex-col items-center justify-center
              h-14 gap-0.5
              transition-colors duration-150
              relative
              ${active ? 'text-nb-accent' : `${theme.colors.text.muted}`}
            `}
          >
            {/* Active indicator bar */}
            {active && <div className="absolute top-0 inset-x-3 h-0.5 bg-nb-accent rounded-full" />}
            <div className="relative">
              <Icon className="w-5 h-5" />
              {/* Badge for docs count */}
              {id === 'docs' && selectedDocsCount > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-nb-accent text-white text-[10px] font-semibold px-1 leading-none">
                  {selectedDocsCount > 99 ? '99+' : selectedDocsCount}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium leading-none">{label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default MobileBottomNav;
